import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { PACKAGES, inr } from "@/lib/packages";

const CATEGORIES = [
  {
    band: "Green",
    label: "Low Risk",
    hex: "#22C55E",
    textHex: "#15803D",
    action: "Reassessment at 1 year",
    to: null,
  },
  {
    band: "Yellow",
    label: "Early Risk",
    hex: "#EAB308",
    textHex: "#A16207",
    action: `Basic evaluation · ${inr(PACKAGES.basic.price)}`,
    to: "/book?tier=basic",
  },
  {
    band: "Orange",
    label: "Moderate Risk",
    hex: "#F97316",
    textHex: "#C2410C",
    action: `Advance evaluation · ${inr(PACKAGES.advanced.price)}`,
    to: "/book?tier=advanced",
  },
  {
    band: "Red",
    label: "High Risk",
    hex: "#EF4444",
    textHex: "#B91C1C",
    action: "Urgent medical attention",
    to: "/book?priority=1",
  },
];

export const ColorRiskCategories = () => {
  return (
    <section id="risk-categories" className="bg-sa-bg py-12 md:py-16" data-testid="risk-categories-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            Color risk categories
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sa-forest sm:text-5xl">
            Your result, and the path it points to.
          </h2>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {CATEGORIES.map((c) => {
            const inner = (
              <>
                <span
                  className="block h-4 w-4 rounded-full"
                  style={{ backgroundColor: c.hex, boxShadow: `0 0 0 7px ${c.hex}2E` }}
                />
                <div className="mt-6 flex items-baseline gap-2">
                  <p className="font-display text-2xl font-semibold" style={{ color: c.textHex }}>{c.band}</p>
                  <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: c.textHex }}>{c.label}</p>
                </div>
                <div
                  className="mt-3 flex items-center gap-1.5 border-t pt-4 text-[14px] font-medium leading-snug text-sa-forest"
                  style={{ borderColor: `${c.hex}40` }}
                >
                  <span className="flex-1">{c.action}</span>
                  {c.to && (
                    <ArrowUpRight
                      className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      style={{ color: c.textHex }}
                    />
                  )}
                </div>
              </>
            );
            const cls =
              "group block rounded-[24px] border p-7 transition-all duration-300 hover:-translate-y-1";
            const style = {
              backgroundColor: `${c.hex}12`,
              borderColor: `${c.hex}55`,
              boxShadow: `0 10px 30px -18px ${c.hex}66`,
            };
            return (
              <motion.div key={c.band} variants={fadeUp} data-testid={`risk-card-${c.band.toLowerCase()}`}>
                {c.to ? (
                  <Link to={c.to} className={cls} style={style} data-testid={`risk-link-${c.band.toLowerCase()}`}>
                    {inner}
                  </Link>
                ) : (
                  <div className={cls} style={style}>{inner}</div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ColorRiskCategories;
